import { Workflow, ExecuteWorkflowRequest, CreateWorkflowRequest, UpdateWorkflowRequest, GetWorkflowExecutionRequest } from '@amzn/finspace-service-typescript-client';
import { FinSpaceServiceApi } from '../api/finspaceServiceApi';
import { handleApiRequest } from '../api/utils/apiRequest';
import { createStore } from './store-factory';

interface WorkflowState {
  workflows: Workflow[];
  workflowTemplates: Workflow[];
  workflowExecution: any;
  isLoading: boolean;
  isTemplatesLoading: boolean;
  isExecutionLoading: boolean;
  isExecuting: boolean;
  isDeleting: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  error: any;
  fetchWorkflows: (spaceId: string) => Promise<void>;
  fetchWorkflowTemplates: (spaceId: string) => Promise<void>;
  getWorkflowExecution: (request: GetWorkflowExecutionRequest) => Promise<any>;
  deleteWorkflow: (spaceId: string, workflowId: string) => Promise<void>;
  executeWorkflow: (request: ExecuteWorkflowRequest) => Promise<void>;
  createWorkflow: (request: CreateWorkflowRequest) => Promise<any>;
  updateWorkflow: (spaceId: string, workflowId: string, request: UpdateWorkflowRequest) => Promise<any>;
  clearError: () => void;
}

export const useWorkflowStore = createStore<WorkflowState>('workflow', (set, get) => ({
  workflows: [],
  workflowTemplates: [],
  workflowExecution: null,
  isLoading: false,
  isTemplatesLoading: false,
  isExecutionLoading: false,
  isExecuting: false,
  isDeleting: false,
  isCreating: false,
  isUpdating: false,
  error: null,

  fetchWorkflows: async (spaceId: string) => {
    if (!spaceId) return;
    
    set({ isLoading: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.getWorkflows({ spaceId }));
      set({ workflows: result?.workflows || [], isLoading: false });
    } catch (error) {
      set({ error, isLoading: false });
    }
  },

  fetchWorkflowTemplates: async (spaceId: string) => {
    if (!spaceId) return;
    
    set({ isTemplatesLoading: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.getWorkflowTemplates({ spaceId }));
      set({ workflowTemplates: result?.workflows || [], isTemplatesLoading: false });
    } catch (error) {
      set({ error, isTemplatesLoading: false });
    }
  },

  deleteWorkflow: async (spaceId: string, workflowId: string) => {
    set({ isDeleting: true, error: null });
    try {
      await handleApiRequest(() => FinSpaceServiceApi.deleteWorkflow(spaceId, workflowId));
      // Remove from local state
      const { workflows } = get();
      set({ workflows: workflows.filter(w => w.id !== workflowId), isDeleting: false });
    } catch (error) {
      set({ error, isDeleting: false });
    }
  },

  executeWorkflow: async (request: ExecuteWorkflowRequest) => {
    set({ isExecuting: true, error: null });
    try {
      await handleApiRequest(() => FinSpaceServiceApi.executeWorkflow(request));
      set({ isExecuting: false });
    } catch (error) {
      set({ error, isExecuting: false });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  getWorkflowExecution: async (request: GetWorkflowExecutionRequest) => {
    set({ isExecutionLoading: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.getWorkflowExecution(request));
      set({ workflowExecution: result, isExecutionLoading: false });
      return result;
    } catch (error) {
      set({ error, isExecutionLoading: false });
    }
  },

  createWorkflow: async (request: CreateWorkflowRequest) => {
    set({ isCreating: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.createWorkflow(request));
      set({ isCreating: false });
      return result;
    } catch (error) {
      set({ error, isCreating: false });
    }
  },

  updateWorkflow: async (spaceId: string, workflowId: string, request: UpdateWorkflowRequest) => {
    set({ isUpdating: true, error: null });
    try {
      const result = await handleApiRequest(() => FinSpaceServiceApi.updateWorkflow(spaceId, workflowId, request));
      set({ isUpdating: false });
      return result;
    } catch (error) {
      set({ error, isUpdating: false });
    }
  },
}));
